// Canonical party-name normalisation.
//
// Single source of truth for how raw party labels (whatever shape they
// arrive in — XLSX, Markdown, admin form) get mapped to the canonical
// strings we store in `candidates.party`.
//
// The DB stores the full canonical name (e.g. 'Aħwa Maltin', 'Momentum')
// not a short code. Frontend display helpers (e.g. useQuiz.normaliseParty
// in src/lib/useQuiz.ts) translate to short codes for compact UI chips.

export type CanonicalParty =
  | 'PL'
  | 'PN'
  | 'ADPD'
  | 'Momentum'
  | 'Aħwa Maltin'
  | 'Imperium'
  | 'IND'
  | 'Other';

export const ALL_PARTIES: readonly CanonicalParty[] = [
  'PL',
  'PN',
  'ADPD',
  'Momentum',
  'Aħwa Maltin',
  'Imperium',
  'IND',
  'Other',
] as const;

/** Map any raw party label to a canonical name. Order of checks matters:
 *  more specific Maltese spellings come before generic English fallbacks
 *  so "AD PD / Momentum" lands on Momentum, not ADPD. */
export function normalisePartyName(raw: string | null | undefined): CanonicalParty {
  const s = (raw ?? '').trim().toLowerCase();
  if (!s) return 'IND';

  // Aħwa Maltin — check first; the ħ may or may not be present.
  if (s.includes('aħwa') || s.includes('ahwa')) return 'Aħwa Maltin';

  // Momentum — must come before ADPD because the "AD PD / Momentum"
  // multi-party rows should be attributed to Momentum.
  if (s.includes('momentum') || s === 'mom') return 'Momentum';

  // PL — Maltese full name first, then English, then code.
  if (s.includes('laburista') || s.includes('labour') || s === 'pl') return 'PL';

  // PN — Maltese full name first, then English, then code.
  if (s.includes('nazzjonalista') || s.includes('nationalist') || s === 'pn') return 'PN';

  // Imperium — covers "Imperium Europa" and bare "Imperium".
  if (s.includes('imperium')) return 'Imperium';

  // ADPD / Alternattiva / generic green.
  if (
    s === 'adpd' ||
    s.includes('ad pd') ||
    s.includes('alternattiva') ||
    s === 'ad' ||
    s.includes('green')
  ) {
    return 'ADPD';
  }

  if (s.includes('independent') || s === 'ind') return 'IND';

  return 'Other';
}
